import type { EventInstance } from '../types';
import { getEventColor, formatTime, formatDateKey } from '../utils';

interface MonthCellProps {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: EventInstance[];
  maxVisible?: number;
  showEventTimes?: boolean;
  minHeightPx?: number;
  onClickEvent: (event: EventInstance) => void;
  /** Open the day itself, to see the events this cell had no room for. */
  onShowMore: (date: Date) => void;
  /** Part of the day range the user is currently dragging out. */
  isSelected?: boolean;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
  /** Begin dragging a chip to another day. */
  onEventDragStart?: (eventId: string, e: React.MouseEvent) => void;
  /** Id of the chip currently being dragged, for the drag-in-progress styling. */
  draggingEventId?: string | null;
}

export function MonthCell({
  date,
  isCurrentMonth,
  isToday,
  events,
  maxVisible = 3,
  showEventTimes = false,
  minHeightPx = 100,
  onClickEvent,
  onShowMore,
  isSelected = false,
  dimPastEvents = false,
  eventColorPriority = 'event',
  onEventDragStart,
  draggingEventId,
}: MonthCellProps) {
  const visibleEvents = events.slice(0, maxVisible);
  const overflow = events.length - maxVisible;

  return (
    // The day activation gesture is owned by MonthView, which delegates
    // pointer events from the week container so a drag can cross cells.
    <div
      data-calendar-date={formatDateKey(date)}
      style={{ minHeight: minHeightPx }}
      className={`
        flex h-full min-w-0 flex-col overflow-hidden
        border-b border-r p-1 cursor-pointer transition-colors
        ${!isCurrentMonth ? 'bg-muted/30' : ''}
        ${isSelected ? 'bg-primary/20 hover:bg-primary/20' : 'hover:bg-muted/50'}
      `}
    >
      {/* Day number */}
      <div className="flex shrink-0 items-center justify-center mb-0.5">
        <span
          className={`
            text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full
            ${isToday ? 'bg-primary text-primary-foreground' : ''}
            ${!isCurrentMonth ? 'text-muted-foreground' : ''}
          `}
        >
          {date.getDate()}
        </span>
      </div>

      {/* Event chips */}
      <div className="min-h-0 flex-1 space-y-0.5 overflow-hidden">
        {visibleEvents.map((event) => {
          const metadata = (event.metadata || {}) as Record<string, unknown>;
          const isFocusTime = metadata.focusTime === true;
          const color = getEventColor(event, eventColorPriority);
          const isPast = dimPastEvents && new Date(event.endAt) < new Date();
          const timePrefix =
            showEventTimes && !event.isAllDay ? formatTime(new Date(event.startAt)) + ' ' : '';

          return (
            <button
              key={event.id}
              type="button"
              className={`block w-full min-w-0 text-[10px] leading-tight truncate rounded px-1 py-0.5 cursor-pointer hover:opacity-80 text-left ${isPast ? 'opacity-50' : ''}`}
              // MonthView's day-range gesture deliberately ignores anything
              // inside a button, so starting a drag here cannot also start a
              // date selection.
              onMouseDown={(e) => onEventDragStart?.(event.id, e)}
              style={{
                // No transform preview: a month chip is laid out inside its
                // cell, so dragging the element itself would clip at the cell
                // boundary. Dimming reads as "this one is moving" without
                // pretending the chip is under the cursor.
                ...(draggingEventId === event.id
                  ? { opacity: 0.4, pointerEvents: 'none' as const }
                  : {}),
                backgroundColor: isFocusTime ? 'transparent' : color + '20',
                color,
                borderLeft: `2px ${isFocusTime ? 'dashed' : 'solid'} ${color}`,
                ...(isFocusTime
                  ? {
                      backgroundImage: `repeating-linear-gradient(135deg, ${color}10, ${color}10 2px, transparent 2px, transparent 4px)`,
                    }
                  : {}),
              }}
              onClick={(e) => {
                e.stopPropagation();
                onClickEvent(event);
              }}
              title={event.title}
            >
              {timePrefix}
              {event.title}
            </button>
          );
        })}

        {overflow > 0 && (
          // A button, which the day-selection handler skips over — so this
          // opens the day instead of starting an event on it.
          <button
            type="button"
            className="block w-full min-w-0 truncate rounded px-1 text-left text-[10px] font-medium text-muted-foreground hover:text-foreground hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              onShowMore(date);
            }}
            title={`Show all ${events.length} events`}
          >
            +{overflow} more
          </button>
        )}
      </div>
    </div>
  );
}
